import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import type { OptionType } from '@/api/common';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
  type ComboboxProps,
} from '@/components/Form/pickers';

export type ComboboxPickerSingleProps<T extends OptionType> = Omit<
  ComboboxProps<T, false>,
  | 'items'
  | 'filteredItems'
  | 'value'
  | 'onValueChange'
  | 'itemToStringValue'
  | 'itemToStringLabel'
  | 'isItemEqualToValue'
> & {
  items?: readonly T[];
  filteredItems?: readonly T[];
  value?: T | null;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  placeholder?: string;
  notFoundContent?: ReactNode;
  renderOption?: (option: T) => ReactNode;
  onBlur?: () => void;
};

const ComboboxPickerSingle = <T extends OptionType>({
  'aria-invalid': ariaInvalid = false,
  items,
  filteredItems,
  value,
  className,
  disabled = false,
  isLoading = false,
  error,
  placeholder = t`Select...`,
  notFoundContent,
  renderOption,
  onBlur,
  ...rest
}: ComboboxPickerSingleProps<T>) => {
  return (
    <Combobox
      items={items}
      filteredItems={filteredItems}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      disabled={disabled}
      isLoading={isLoading}
      error={error}
      value={value ?? null}
      {...rest}
    >
      <ComboboxInput
        className={className}
        placeholder={placeholder}
        onBlur={onBlur}
        aria-invalid={ariaInvalid || undefined}
      />
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent ?? <Trans>No items were found...</Trans>}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {renderOption ? renderOption(item) : item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default ComboboxPickerSingle;
